import { CalendarDays, Clock3, Infinity as InfinityIcon, TimerReset } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { NumberInput } from '@/components/ui/number-input';
import { cn } from '@/lib/utils';

export type PassExpiryDraft = {
  mode: 'absolute' | 'duration';
  expiresAt: string;
  durationDays: number;
  durationHours: number;
};

type PassExpiryPickerProps = {
  value: PassExpiryDraft;
  onChange: (value: PassExpiryDraft) => void;
  disabled?: boolean;
  className?: string;
  durationMode?: 'extend' | 'from-now';
};

const PRESETS = [
  { label: '7 дней', days: 7 },
  { label: '30 дней', days: 30 },
  { label: '90 дней', days: 90 },
  { label: '1 год', days: 365 },
] as const;

function pad(value: number): string {
  return String(value).padStart(2, '0');
}

function toLocalDateTimeValue(date: Date): string {
  return [
    date.getFullYear(),
    '-',
    pad(date.getMonth() + 1),
    '-',
    pad(date.getDate()),
    'T',
    pad(date.getHours()),
    ':',
    pad(date.getMinutes()),
  ].join('');
}

function nextRoundedTime(): Date {
  const date = new Date();
  date.setSeconds(0, 0);
  date.setMinutes(Math.ceil(date.getMinutes() / 5) * 5);
  return date;
}

function createAbsoluteExpiry(days = 30): string {
  const date = nextRoundedTime();
  date.setDate(date.getDate() + days);
  return toLocalDateTimeValue(date);
}

export function createPassExpiryDraft(): PassExpiryDraft {
  return {
    mode: 'absolute',
    expiresAt: createAbsoluteExpiry(),
    durationDays: 30,
    durationHours: 0,
  };
}

export function isPassExpiryValid(value: PassExpiryDraft): boolean {
  if (value.mode === 'duration') {
    return (
      Number.isInteger(value.durationDays) &&
      value.durationDays >= 0 &&
      value.durationDays <= 3650 &&
      Number.isInteger(value.durationHours) &&
      value.durationHours >= 0 &&
      value.durationHours <= 23 &&
      value.durationDays + value.durationHours > 0
    );
  }
  if (!value.expiresAt) return true;
  const timestamp = new Date(value.expiresAt).getTime();
  return Number.isFinite(timestamp) && timestamp > Date.now();
}

export function toPassExpiryPayload(value: PassExpiryDraft):
  | { expiresAt: string | null }
  | { durationDays: number; durationHours: number } {
  if (value.mode === 'duration') {
    return {
      durationDays: value.durationDays,
      durationHours: value.durationHours,
    };
  }
  return {
    expiresAt: value.expiresAt ? new Date(value.expiresAt).toISOString() : null,
  };
}

export function PassExpiryPicker({
  value,
  onChange,
  disabled = false,
  className,
  durationMode = 'extend',
}: PassExpiryPickerProps) {
  const [datePart = '', rawTimePart = ''] = value.expiresAt.split('T');
  const timePart = rawTimePart.slice(0, 5);
  const parsed = value.expiresAt ? new Date(value.expiresAt) : null;
  const isInvalid = Boolean(value.expiresAt) && (!parsed || Number.isNaN(parsed.getTime()));
  const isPast = Boolean(parsed && !isInvalid && parsed.getTime() <= Date.now());
  const durationInvalid = value.mode === 'duration' && !isPassExpiryValid(value);
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  function patch(next: Partial<PassExpiryDraft>) {
    onChange({ ...value, ...next });
  }

  function updateDate(nextDate: string) {
    if (!nextDate) {
      patch({ expiresAt: '' });
      return;
    }
    const fallbackTime = toLocalDateTimeValue(nextRoundedTime()).slice(11, 16);
    patch({ expiresAt: `${nextDate}T${timePart || fallbackTime}` });
  }

  function updateTime(nextTime: string) {
    if (!nextTime) {
      if (datePart) patch({ expiresAt: `${datePart}T00:00` });
      return;
    }
    const fallbackDate = toLocalDateTimeValue(nextRoundedTime()).slice(0, 10);
    patch({ expiresAt: `${datePart || fallbackDate}T${nextTime}` });
  }

  return (
    <div className={cn('space-y-3', className)}>
      <div
        className="grid grid-cols-2 gap-1 rounded-lg border border-white/10 bg-eol-black/40 p-1"
        role="group"
        aria-label="Способ задания срока проходки"
      >
        <Button
          type="button"
          size="sm"
          variant={value.mode === 'absolute' ? 'default' : 'ghost'}
          disabled={disabled}
          onClick={() => patch({ mode: 'absolute' })}
          className="gap-1.5"
        >
          <CalendarDays className="h-4 w-4" /> Выдать до даты
        </Button>
        <Button
          type="button"
          size="sm"
          variant={value.mode === 'duration' ? 'default' : 'ghost'}
          disabled={disabled}
          onClick={() => patch({ mode: 'duration' })}
          className="gap-1.5"
        >
          <TimerReset className="h-4 w-4" />
          {durationMode === 'from-now' ? 'Выдать на срок' : 'Добавить срок'}
        </Button>
      </div>

      {value.mode === 'duration' ? (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <label className="flex flex-col gap-1.5">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-eol-desc">
                Дни
              </span>
              <NumberInput
                value={value.durationDays}
                onChange={(durationDays) => patch({ durationDays: Math.trunc(durationDays) })}
                min={0}
                max={3650}
                disabled={disabled}
                className="w-full"
              />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-eol-desc">
                Часы
              </span>
              <NumberInput
                value={value.durationHours}
                onChange={(durationHours) => patch({ durationHours: Math.trunc(durationHours) })}
                min={0}
                max={23}
                disabled={disabled}
                className="w-full"
              />
            </label>
          </div>
          <div
            className={cn(
              'rounded-md border px-3 py-2 text-xs',
              durationInvalid
                ? 'border-eol-red/30 bg-eol-red/5 text-eol-red'
                : 'border-white/10 bg-eol-black/30 text-eol-desc',
            )}
            aria-live="polite"
          >
            {durationInvalid ? (
              <span>Укажите хотя бы один день или один час.</span>
            ) : (
              <span>
                {durationMode === 'from-now' ? 'Проходка будет действовать ' : 'К сроку будет добавлено '}
                <strong className="font-semibold text-eol-white">
                  {value.durationDays > 0 && `${value.durationDays} дн.`}
                  {value.durationDays > 0 && value.durationHours > 0 && ' '}
                  {value.durationHours > 0 && `${value.durationHours} ч.`}
                </strong>
                {durationMode === 'from-now'
                  ? ' с текущего момента.'
                  : '. Если активной проходки нет, срок начнётся от текущего момента.'}
              </span>
            )}
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex flex-wrap gap-2">
            {PRESETS.map((preset) => (
              <Button
                key={preset.days}
                type="button"
                size="sm"
                variant="secondary"
                disabled={disabled}
                onClick={() => patch({ expiresAt: createAbsoluteExpiry(preset.days) })}
              >
                +{preset.label}
              </Button>
            ))}
            <Button
              type="button"
              size="sm"
              variant={value.expiresAt ? 'outline' : 'secondary'}
              disabled={disabled}
              onClick={() => patch({ expiresAt: '' })}
              className="gap-1.5"
            >
              <InfinityIcon className="h-4 w-4" /> Бессрочно
            </Button>
          </div>

          <div className="grid gap-2 sm:grid-cols-2">
            <label className="flex flex-col gap-1.5">
              <span className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-eol-desc">
                <CalendarDays className="h-3.5 w-3.5" /> Дата окончания
              </span>
              <Input
                type="date"
                value={datePart}
                disabled={disabled || !value.expiresAt}
                min={toLocalDateTimeValue(new Date()).slice(0, 10)}
                onChange={(event) => updateDate(event.target.value)}
                className="[color-scheme:dark]"
              />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-eol-desc">
                <Clock3 className="h-3.5 w-3.5" /> Время окончания
              </span>
              <Input
                type="time"
                value={timePart}
                disabled={disabled || !value.expiresAt}
                step={300}
                onChange={(event) => updateTime(event.target.value)}
                className="[color-scheme:dark]"
              />
            </label>
          </div>

          <div
            className={cn(
              'rounded-md border px-3 py-2 text-xs',
              isInvalid || isPast
                ? 'border-eol-red/30 bg-eol-red/5 text-eol-red'
                : 'border-white/10 bg-eol-black/30 text-eol-desc',
            )}
            aria-live="polite"
          >
            {!value.expiresAt ? (
              <span>Проходка будет действовать бессрочно.</span>
            ) : isInvalid ? (
              <span>Укажите корректные дату и время.</span>
            ) : isPast ? (
              <span>Дата окончания должна быть в будущем.</span>
            ) : (
              <span>
                Действует до{' '}
                <strong className="font-semibold text-eol-white">
                  {parsed!.toLocaleString('ru-RU', { dateStyle: 'long', timeStyle: 'short' })}
                </strong>
                {timezone && <span className="text-eol-desc"> · {timezone}</span>}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
